<template>
  <div class="compliance-status-card">
    <div v-if="loading" class="text-center py-2"><i class="fa fa-spinner fa-spin"></i></div>
    <div v-else>
      <div
        v-for="row in unifiedRows"
        :key="row.type"
        class="compliance-status-card__row"
      >
        <!-- Overall status dot -->
        <span class="compliance-status-card__dot" :class="`compliance-status-card__dot--${row.overallStatus}`"></span>

        <!-- Type name -->
        <span class="compliance-status-card__form-name">{{ row.displayName }}</span>

        <!-- E-signature status -->
        <template v-if="row.form">
          <el-tooltip
            v-if="row.form.status === 'partially_signed' && row.form.signerRoles"
            :content="partialSignedTooltip(row.form.signerRoles)"
            placement="top"
          >
            <el-tag size="mini" type="warning">partially signed</el-tag>
          </el-tooltip>
          <el-tag v-else-if="row.form.status === 'in_person'" size="mini" type="success">in-person</el-tag>
          <el-tag
            v-else-if="(row.form.status === 'missing' || row.form.status === 'expired') && row.doc && row.doc.status === 'active'"
            size="mini"
            type="success"
          >via document</el-tag>
          <el-tag v-else size="mini" :type="formStatusTagType(row.form.status)">{{ row.form.status }}</el-tag>

          <el-tooltip v-if="row.form.pdfFilename" content="View signed form" placement="top">
            <i class="fa fa-file-pdf compliance-status-card__view-icon" @click.stop="viewForm(row.form)"></i>
          </el-tooltip>
          <el-tooltip
            v-if="row.form.status === 'missing' || row.form.status === 'expired'"
            content="Mark as satisfied (in-person)"
            placement="top"
          >
            <i class="fa fa-file-upload compliance-status-card__action-icon" @click.stop="openSatisfyModal(row.form)"></i>
          </el-tooltip>
          <span v-if="row.form.signedAt" class="compliance-status-card__date">
            {{ formatDate(row.form.signedAt) }}
          </span>
        </template>

        <!-- Document status -->
        <template v-if="row.doc">
          <el-tag v-if="row.doc.status === 'waived'" size="mini" type="warning">waived</el-tag>
          <template v-else-if="row.doc.document && row.doc.document.file_path">
            <el-tooltip content="View uploaded document" placement="top">
              <i class="fa fa-file compliance-status-card__view-icon" @click.stop="$emit('view-doc', row.doc.document.id)"></i>
            </el-tooltip>
            <span v-if="row.doc.document.expiration_date" class="compliance-status-card__date">
              exp {{ row.doc.document.expiration_date }}
            </span>
          </template>
        </template>

        <!-- Document action buttons -->
        <template v-if="row.doc && canEdit">
          <el-tooltip v-if="row.doc.document && row.doc.document.id" content="Edit document" placement="top">
            <i class="el-icon-edit compliance-status-card__action-icon" @click.stop="$emit('edit-doc', row.doc)"></i>
          </el-tooltip>
          <el-tooltip v-if="row.doc.document && row.doc.document.id && row.doc.document.file_path" content="Delete document" placement="top">
            <i class="el-icon-delete compliance-status-card__action-icon compliance-status-card__action-icon--danger" @click.stop="$emit('delete-doc', row.doc.document.id, row.doc.type)"></i>
          </el-tooltip>
          <el-tooltip :content="row.doc.status === 'waived' ? 'Remove waiver' : 'Waive this item'" placement="top">
            <i
              :class="row.doc.status === 'waived' ? 'fa fa-undo' : 'fa fa-ban'"
              class="compliance-status-card__action-icon"
              :style="row.doc.status === 'waived' ? 'color: #FF9800;' : ''"
              @click.stop="$emit('toggle-waiver', row.doc)"
            ></i>
          </el-tooltip>
        </template>
      </div>

      <div v-if="unifiedRows.length === 0" class="text-center" style="color: #b3a6a6; font-size: 0.85rem;">
        No compliance data.
      </div>
    </div>

    <SatisfyComplianceFormModal
      :visible.sync="showSatisfyModal"
      :clientId="clientId"
      :formId="satisfyFormId"
      :formName="satisfyFormName"
      :existingDocuments="existingDocuments"
      @satisfied="onSatisfied"
    />
  </div>
</template>

<script>
import { complianceFormsService } from "@/serviceHandlers/complianceFormsService";
import SatisfyComplianceFormModal from "@/components/SatisfyComplianceFormModal";
import dayjs from "dayjs";

// Maps form display_name -> client compliance document type
const FORM_TO_DOC_TYPE = {
  "Consent & Agreement": "Consent",
  "Rules & Expectations": "Rules",
  "Suicide/Self-harm": "Suicide/Self-Harm",
  // These match exactly and don't strictly need entries, but included for clarity
  "Grievance": "Grievance",
  "Human Rights": "Human Rights",
  "Liability": "Liability",
  "Media Release": "Media Release",
  "Eligibility Screening": "Eligibility Screening",
  "Fee Agreement": "Fee Agreement",
};

function formNameToDocType(formName) {
  return FORM_TO_DOC_TYPE[formName] || formName;
}

export default {
  name: "ComplianceStatusCard",
  components: { SatisfyComplianceFormModal },
  props: {
    clientId: { type: Number, required: true },
    existingDocuments: { type: Array, default: () => [] },
    complianceStatus: { type: Array, default: () => [] },
    canEdit: { type: Boolean, default: false },
  },
  data() {
    return {
      formStatuses: [],
      loading: false,
      showSatisfyModal: false,
      satisfyFormId: null,
      satisfyFormName: "",
    };
  },
  computed: {
    unifiedRows() {
      const rows = [];
      const docByType = {};

      // Index documents by type
      for (const doc of this.complianceStatus) {
        docByType[doc.type] = doc;
      }

      const usedDocTypes = new Set();

      // Start with form entries, merging in matching docs
      for (const form of this.formStatuses) {
        const docType = formNameToDocType(form.formName);
        const doc = docByType[docType] || null;
        if (doc) usedDocTypes.add(docType);

        rows.push({
          type: docType,
          displayName: form.formName,
          form,
          doc,
          overallStatus: this.computeOverallStatus(form, doc),
        });
      }

      // Add any doc types that didn't have a matching form
      for (const doc of this.complianceStatus) {
        if (!usedDocTypes.has(doc.type)) {
          rows.push({
            type: doc.type,
            displayName: doc.type,
            form: null,
            doc,
            overallStatus: this.computeDocOnlyStatus(doc),
          });
        }
      }

      return rows;
    },
  },
  watch: {
    clientId: {
      immediate: true,
      handler(val) { if (val) this.fetch(); },
    },
  },
  methods: {
    async fetch() {
      this.loading = true;
      try {
        this.formStatuses = await complianceFormsService.getClientCompliance(this.clientId);
      } catch (e) { this.formStatuses = []; }
      finally { this.loading = false; }
    },
    computeOverallStatus(form, doc) {
      // If form is signed/in_person, that's the primary indicator
      if (form.status === "signed" || form.status === "in_person") {
        // Check if doc is expired even though form is signed
        if (doc && doc.status === "expired") return "expiring";
        return "signed";
      }
      if (form.status === "expiring") return "expiring";
      if (form.status === "expired") return "expired";
      if (form.status === "partially_signed") return "partially_signed";
      // Form is pending or missing — check doc
      if (doc && doc.status === "waived") return "waived";
      if (doc && doc.status === "active") return "signed";
      if (form.status === "pending") return "pending";
      return "missing";
    },
    computeDocOnlyStatus(doc) {
      if (doc.status === "active") return "signed";
      if (doc.status === "waived") return "waived";
      if (doc.status === "expired") return "expired";
      return "missing";
    },
    formStatusTagType(status) {
      var map = { signed: "success", in_person: "success", expiring: "warning", expired: "danger", pending: "info", missing: "info", partially_signed: "warning" };
      return map[status] || "info";
    },
    partialSignedTooltip(roles) {
      if (!roles) return "";
      return roles.map(function(r) { return (r.role === "guardian" ? "Guardian" : "Client") + ": " + r.status; }).join(", ");
    },
    formatDate(d) {
      return d ? dayjs(d).format("MM/DD/YYYY") : "";
    },
    viewForm(form) {
      if (form.pdfFilename) {
        this.$emit("view-pdf", { filename: form.pdfFilename, displayName: form.formName + " - Signed" });
      }
    },
    openSatisfyModal(form) {
      this.satisfyFormId = form.formId;
      this.satisfyFormName = form.formName;
      this.showSatisfyModal = true;
    },
    onSatisfied() {
      this.fetch();
      this.$emit("refresh");
    },
  },
};
</script>

<style scoped>
.compliance-status-card { background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.1); border-radius: 8px; padding: 1rem; }
.compliance-status-card__row { display: flex; align-items: center; gap: 0.5rem; padding: 0.45rem 0.25rem; border-bottom: 1px solid rgba(255, 255, 255, 0.06); }
.compliance-status-card__row:last-child { border-bottom: none; }
.compliance-status-card__dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
.compliance-status-card__dot--signed { background: #67c23a; }
.compliance-status-card__dot--in_person { background: #67c23a; }
.compliance-status-card__dot--expiring { background: #e6a23c; }
.compliance-status-card__dot--expired { background: #f56c6c; }
.compliance-status-card__dot--pending { background: #909399; }
.compliance-status-card__dot--missing { background: #404040; border: 1px solid #606060; }
.compliance-status-card__dot--partially_signed { background: #e6a23c; }
.compliance-status-card__dot--partial { background: #409eff; }
.compliance-status-card__dot--waived { background: #FF9800; }
.compliance-status-card__form-name { flex: 1; color: #e0e0e0; font-size: 0.85rem; }
.compliance-status-card__view-icon { color: #447DF7; font-size: 0.9rem; flex-shrink: 0; cursor: pointer; }
.compliance-status-card__action-icon { color: #e6a23c; font-size: 0.9rem; flex-shrink: 0; cursor: pointer; }
.compliance-status-card__action-icon:hover { color: #f0c060; }
.compliance-status-card__action-icon--danger { color: #f56c6c; }
.compliance-status-card__action-icon--danger:hover { color: #ff8080; }
.compliance-status-card__date { color: #b3a6a6; font-size: 0.75rem; flex-shrink: 0; }
</style>
